import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { NTA_COLORS, NTA_FONT_ARIAL_BLACK } from '../constants';
import { ntaButtonCss, ntaPageCss } from '../typography';

const ALERT_ICON = '/nta-experience/alert-circle.svg';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1100;
  background: ${NTA_COLORS.warningOverlay};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Modal = styled.div`
  width: 482px;
  max-width: calc(100% - 32px);
  background: ${NTA_COLORS.white};
  border-radius: 4px;
  padding: 27px 0;
  overflow: hidden;
  ${ntaPageCss}
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 28px;
  padding: 0 24px;
`;

const Icon = styled.img`
  width: 90px;
  height: 90px;
`;

const TextBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  text-align: center;
  width: 100%;
`;

const Title = styled.p`
  margin: 0;
  --nta-font-family: ${NTA_FONT_ARIAL_BLACK};
  font-size: 27px;
  font-weight: 900;
  line-height: 32px;
  letter-spacing: 0;
  color: ${NTA_COLORS.warningTitle};
`;

const Message = styled.p`
  margin: 0;
  font-size: 20px;
  font-weight: 400;
  line-height: 25px;
  letter-spacing: 0;
  color: ${NTA_COLORS.warningMessage};
`;

const OkButton = styled.button`
  height: 48px;
  padding: 0 28px;
  border: none;
  border-radius: 6px;
  background: ${NTA_COLORS.warningButton};
  color: ${NTA_COLORS.white};
  cursor: pointer;
  ${ntaButtonCss}

  &:focus-visible {
    outline: 2px solid ${NTA_COLORS.accentBlue};
    outline-offset: 2px;
  }
`;

const NtaWarningModal = ({ open, title, message, onClose }) => {
  if (!open || typeof document === 'undefined') {
    return null;
  }

  return createPortal(
    <Overlay role="presentation" onClick={onClose}>
      <Modal
        role="alertdialog"
        aria-labelledby="nta-warning-title"
        aria-describedby="nta-warning-message"
        onClick={(event) => event.stopPropagation()}
      >
        <Content>
          <Icon src={ALERT_ICON} alt="" aria-hidden />
          <TextBlock>
            <Title id="nta-warning-title">{title}</Title>
            <Message id="nta-warning-message">{message}</Message>
          </TextBlock>
          <OkButton type="button" onClick={onClose}>
            OK
          </OkButton>
        </Content>
      </Modal>
    </Overlay>,
    document.body
  );
};

NtaWarningModal.propTypes = {
  open: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
  message: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired
};

export default NtaWarningModal;
